/**
 * @file interpret/InterpretPage.tsx
 * @brief Live interpretation on web + desktop (`/app/interpret`, `/company/interpret`).
 *
 * @details
 * DES, 01-10-2026: "boardroom recording wali screen banai hai usi tarha bana lo".
 * So the page follows the web Boardroom flow, in two modes on one route:
 *
 * 1. Setup (as `BoardroomSetupPage`): back link + heading + microphone on the
 *    left, the form on the right (languages for each person, Person B, reference
 *    number), then "Slide to Start Interpretation". The slide is also the user
 *    gesture that unlocks audio playback (autoplay rules).
 * 2. Live (as `BoardroomRecorderPage`): on desktop the controls are one sheet on
 *    the left (status pill, title, language pills, the ring mic with its timer,
 *    status line, caption, End turn, "Slide to End Interpretation"); the live
 *    transcript takes the right column, where the recorder shows its agenda.
 *    Phones: one column with the controls as a tray at the bottom.
 *
 * Behaviour is the phone's Silero screen (`SileroConversationScreen`):
 * - mic tap = pause / resume (the recorder's hold); pause is refused while a
 *   translation plays; End turn (or Space) = the noisy-room manual end;
 * - the phone's status wording, noise warning and RTT pill;
 * - Person A defaults to the signed-in user; languages can't share a base code;
 * - the cost is not shown (the phone hides it too, `showSessionCost={false}`).
 * After End the user lands on the session's detail page, as Boardroom lands on
 * its meeting.
 *
 * All session logic is `engine/interpretController.ts`; this file only renders.
 *
 * @author DES
 * @date 01-10-2026
 */

import { Switch } from "@/components/ui/Switch";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CircleAlert,
  Clock,
  CloudOff,
  Hand,
  Hourglass,
  Languages,
  LoaderCircle,
  Mars,
  Mic,
  Pause,
  Play,
  User,
  Venus,
  Volume2,
  Zap,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Avatar } from "@/components/ui/Avatar";
import {
  FormFieldGroup,
  FormSheetField,
  FormSheetFields,
  formGridLinesHiddenClass,
} from "@/components/ui/FormSheet";
import { GenderPicker } from "@/components/ui/GenderPicker";
import { Input } from "@/components/ui/Input";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { BrandMarkSlideIcon } from "@/components/brand/BrandMarkGlyph";
import { useAuth } from "@/shared/auth/auth-context";
import { GENDERS } from "@/shared/data/genders";
import { billingAudienceFor, billingErrorView, type BillingAudience } from "@/features/my-billing/billingErrors";
import { toast } from "@/shared/hooks/useToast";
import { useDesktopSession } from "@/shared/desktop/useDesktopSession";
import { isRTLText } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import {
  getLanguageEnglishName,
  languageBaseCode,
  sameLanguageBase,
} from "@/shared/utils/languages";
import { speakerInitials } from "@/features/sessions/components/SessionDetailDialog";
import { describeMicError } from "@/features/employee-boardroom/capture/micErrors";
import { normalizeMicRms } from "@/features/employee-boardroom/capture/micMeter";
import { useMicDevices } from "@/features/employee-boardroom/capture/useMicDevices";
import { useDesktopDefaultMic } from "@/shared/desktop/useDesktopSettings";
import { canCaptureSystemAudio } from "@/features/employee-boardroom/capture/systemAudio";
import { CallModeControls } from "./components/CallModeControls";
import { useCallSettings, type CallSettings } from "./components/callSettings";
import {
  hearDeviceFor,
  hearsOnSpeakers,
  micForCapture,
  micsWithoutCallMic,
  sendsToVerblizrMic,
} from "./components/callMicDevices";
import { useCallMicDefault } from "./components/useCallMicDefault";
import { useOutputDevices } from "./components/useOutputDevices";
import { useCallApps } from "./components/useCallApps";
import { FlipClock } from "@/features/employee-boardroom/FlipClock";
import { MicPicker, describeMicInUse } from "@/features/employee-boardroom/MicPicker";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import { useRecorderLeaveGuard } from "@/features/employee-boardroom/useRecorderLeaveGuard";
import { InterpretMicButton, type InterpretMicState } from "./components/InterpretMicButton";
import { PREVIEW_TOASTS_CYCLE } from "./dev/previewSwitches";

/**
 * [09-10-2026 TRIAL] The phone's Interpreter pause control on the web. DES
 * rejected a full-width bar ("way too bad") and picked a ROUND Pause/Play
 * button under the mic, the live status under it. Paused: the status line
 * reads "Click here to resume" and the mic shows mic-off. The transcript
 * already sits beside the recorder, so no transcript button. false = the old
 * layout (clickable status line under the mic) exactly.
 */
const INTERPRET_DOCK = true;

/** The noisy-room advice as a toast (DES 09-10-2026, "yeh bhi toast main"),
 *  so it no longer grows the recorder sheet and shifts the mic. false = the
 *  old inline line under the controls. */
const NOISE_AS_TOAST = true;

/**
 * DEV ONLY (09-10-2026, DES "show me the toast warnings on desktop"): with
 * `?toasts=1` the screen shows every toast it can raise, one at a time, so
 * their look can be checked in `/__preview/interpret`. Production builds
 * drop it (`import.meta.env.DEV`).
 */
const PREVIEW_TOASTS: Array<{ title: string; description?: string; variant?: "warning" | "success" }> = [
  { title: "Ending turn…" },
  { title: "Pause the session to play transcript audio." },
  { title: "No audio available for this turn." },
  { title: "We couldn't play that translation. It is in the transcript." },
  { title: "We couldn't send your voice to the call. Translations still play here." },
  { title: "Your microphone was disconnected. Reconnect it, then tap to resume." },
  { title: "Paused after 10 minutes of silence, so you are not charged. Tap the mic to resume." },
  { variant: "warning", title: "Select languages", description: "Please select languages for both speakers to begin." },
  { variant: "warning", title: "Both speakers can’t use the same language." },
  { variant: "success", title: "Session Completed" },
  { variant: "warning", title: "Session ended", description: "The connection was lost. Your transcript is saved." },
];
import { InterpretTranscript } from "./components/InterpretTranscript";
import { VoiceConsentSheet } from "./components/VoiceConsentSheet";

import type {
  InterpretDeps,
  InterpretPhase,
  InterpretSnapshot,
} from "./engine/interpretController";
import { useInterpretSession } from "./engine/useInterpretSession";
import { companyIdOf } from "./pilot";
import { useHomeScheduled, useHomeSessions } from "@/features/home/homeApi";
import { formatElapsed, nextSpeaking, rttLabel, statusView, type StatusIcon } from "./interpretUi";
import { ensureAiConsent } from "@/features/ai-consent/consentBridge";

/** A booked session handed over by a "Join" link (router state). */
export type InterpretBooking = {
  id: string;
  source_language?: string | null;
  target_language?: string | null;
  person_b_name?: string | null;
  person_a_gender?: string | null;
  person_b_gender?: string | null;
  reference_number?: string | null;
};

type NavState = { booking?: InterpretBooking } | null;

/** Desktop mic sheet's minimum height in px (DES 01-10-2026: a little taller). */
const SHEET_MIN_H = 640;

const LIVE: ReadonlySet<InterpretPhase> = new Set([
  "starting",
  "listening",
  "translating",
  "playing",
  "paused",
  "ending",
]);

/** Same as the recorder: the controls sheet / tray layout switches. */
function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

/** Air kept between the sheet's top and the shell header (recorder value). */
const STICKY_GAP = 24;
/** Mic level that fills the glow (AEC/AGC capture runs hotter than raw). */
const GLOW_CEILING = 0.12;

const LAST_PAIR_KEY = "verblizr.interpret.lastPair";

function readLastPair(): { a: string | null; b: string | null } {
  try {
    const raw = window.localStorage.getItem(LAST_PAIR_KEY);
    const v = raw ? (JSON.parse(raw) as { a?: unknown; b?: unknown }) : {};
    return {
      a: typeof v.a === "string" ? v.a : null,
      b: typeof v.b === "string" ? v.b : null,
    };
  } catch {
    return { a: null, b: null };
  }
}

/**
 * The regional locale sent with the session (DES 01-10-2026: UK English played
 * in a US voice on web, fine on mobile). A picker value that already carries a
 * region ("en-GB") is used as is; a bare "en" gets the browser's English
 * region, else en-GB, as mobile's `defaultLocaleForLang` does.
 */
function sessionLocale(lang: string): string | undefined {
  if (lang.includes("-")) return lang;
  if (languageBaseCode(lang) !== "en") return undefined;
  const browser = (typeof navigator !== "undefined" ? navigator.languages : []).find((l) =>
    /^en-[A-Z]{2}$/i.test(l),
  );
  return browser ? `en-${browser.slice(3).toUpperCase()}` : "en-GB";
}

function saveLastPair(a: string, b: string): void {
  try {
    window.localStorage.setItem(LAST_PAIR_KEY, JSON.stringify({ a, b }));
  } catch {
    /* a convenience only */
  }
}

const STATUS_ICONS: Record<StatusIcon, typeof Mic> = {
  hourglass: Hourglass,
  "cloud-off": CloudOff,
  hand: Hand,
  languages: Languages,
  volume: Volume2,
  mic: Mic,
  loader: LoaderCircle,
};

export function InterpretPage({
  createDeps,
  previewCallApps,
  previewAutoStart = false,
}: {
  /** Dev preview only: start the scripted session at once, English → Urdu
   *  unless a pair is remembered, with no consent sheet (09-10-2026). */
  previewAutoStart?: boolean;
  /** Dev preview only (`dev/InterpretPreview.tsx`): a scripted session. */
  createDeps?: () => InterpretDeps;
  /** Dev preview only: pretend these call apps are in a call, so Call mode
   *  shows without a real call (05-10-2026, `?call=1`). */
  previewCallApps?: string[];
} = {}) {
  const { user } = useAuth();
  const base = useBoardroomBasePath();
  const navigate = useNavigate();
  const location = useLocation();
  // A booking arrives as router state or `?booking=<id>` (Home's card = the
  // phone's "Join"); the id is looked up in the same cached list Home shows.
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking");
  const scheduled = useHomeScheduled(user?.id, !!bookingId);
  const booking: InterpretBooking | null =
    ((location.state ?? null) as NavState)?.booking ??
    (bookingId
      ? ((scheduled.data?.find((r) => r.id === bookingId) as InterpretBooking | undefined) ?? null)
      : null);
  const { controller, snapshot } = useInterpretSession(createDeps);
  const live = LIVE.has(snapshot.phase);

  // ---- setup form --------------------------------------------------------
  const lastPair = useMemo(readLastPair, []);
  // "Recent" at the top of the language menus, as the phone: last pair on
  // this device, then the languages of the latest sessions (Home's cached
  // list, so usually no extra request). 04-10-2026, DES.
  const recentSessions = useHomeSessions(user?.id, true);
  const recentLangs = useMemo(() => {
    const out: string[] = [];
    const add = (v: string | null | undefined) => {
      if (v && !out.includes(v)) out.push(v);
    };
    add(lastPair.a);
    add(lastPair.b);
    for (const row of recentSessions.data ?? []) {
      add(row.source_language);
      add(row.target_language);
    }
    return out;
  }, [lastPair, recentSessions.data]);
  const [langA, setLangA] = useState<string | null>(booking?.source_language ?? lastPair.a);
  const [langB, setLangB] = useState<string | null>(booking?.target_language ?? lastPair.b);
  const [personBName, setPersonBName] = useState(booking?.person_b_name ?? "");
  const [personBGender, setPersonBGender] = useState<string | null>(
    booking?.person_b_gender ?? null,
  );
  const personAGender = booking?.person_a_gender ?? null;
  const [reference, setReference] = useState(booking?.reference_number ?? "");
  const prefilledFor = useRef<string | null>(booking?.id ?? null);
  useEffect(() => {
    if (!booking || prefilledFor.current === booking.id) return;
    prefilledFor.current = booking.id;
    if (booking.source_language) setLangA(booking.source_language);
    if (booking.target_language) setLangB(booking.target_language);
    if (booking.person_b_name) setPersonBName(booking.person_b_name);
    if (booking.person_b_gender) setPersonBGender(booking.person_b_gender);
    if (booking.reference_number) setReference(booking.reference_number);
  }, [booking]);
  const { devices: allMics, loading: micsLoading, error: micsError } = useMicDevices();
  const [deviceId, setDeviceId] = useState("");
  // Call mode (Zoom / Teams / Meet), desktop app only.
  // Offered only during a call (Zoom / Teams open, a browser in Meet…): a
  // plain session never shows it (DES 03-10-2026). Read on the setup form,
  // kept as it was for the length of a session.
  const detectedCallApps = useCallApps(canCaptureSystemAudio() && !live && !previewCallApps);
  const callApps = previewCallApps ?? detectedCallApps;
  const callModeAvailable = canCaptureSystemAudio() && (callApps === null || callApps.length > 0);
  const [callSettings, setCallSettings] = useCallSettings();
  const callOn = callModeAvailable && callSettings.on;
  // Sending to Verblizr Microphone: it is the Mac's mic while a session
  // runs with Call mode on, so the call app needs no setting (03-10-2026).
  // Only while live (DES: with the app merely open, the call heard
  // nothing): before Start and after End the call has your own mic.
  const routedToCallMic =
    callOn && !!callSettings.sendDeviceId && sendsToVerblizrMic(callSettings.sendLabel);
  // Given back the moment End is pressed (DES: it held on until the whole
  // end-of-session process finished), and while paused, so you can talk to
  // them directly (DES 03-10-2026).
  const micBefore = useCallMicDefault(
    routedToCallMic && live && snapshot.phase !== "ending" && snapshot.phase !== "paused",
  );
  // Where you hear their translation, resolved at Start (headphones plugged
  // in after the page opened count).
  const outputs = useOutputDevices(callOn);
  // Our own capture never hears Verblizr Microphone.
  const devices = useMemo(() => micsWithoutCallMic(allMics, micBefore), [allMics, micBefore]);
  // Desktop app: the mic from Settings (⌘,) is the starting choice.
  useDesktopDefaultMic(devices, deviceId, setDeviceId);

  const personAName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.name?.trim() ||
    "You";
  const personBLabel = personBName.trim() || "Person B";
  const corporate =
    !!companyIdOf(user) &&
    (user?.role === "user" ||
      (user as { activeBillingContext?: unknown } | null)?.activeBillingContext === "corporate");

  const canStart = !!langA && !!langB && !sameLanguageBase(langA, langB);

  const hearDevice = callOn ? hearDeviceFor(outputs, callSettings.hearDeviceId) : null;
  const hearOnSpeakers = hearsOnSpeakers(outputs, hearDevice);
  // Headphones plugged in or out during the session: follow at once.
  useEffect(() => {
    if (!live || !callOn) return;
    controller.setCallOutput({ hearDeviceId: hearDevice, speakers: hearOnSpeakers });
  }, [live, callOn, hearDevice, hearOnSpeakers, controller]);

  // DEV preview only: straight into a live scripted session (previewAutoStart).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!import.meta.env.DEV || !previewAutoStart || autoStarted.current) return;
    if (!langA) return setLangA("en-GB");
    if (!langB) return setLangB("ur-PK");
    // A tick later, so a dev StrictMode remount can't start the session on
    // the controller its first cleanup ends.
    const t = setTimeout(() => {
      autoStarted.current = true;
      beginSession(true);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewAutoStart, langA, langB]);

  const onStart = async () => {
    if (!langA || !langB) {
      toast({
        variant: "warning",
        title: "Select languages",
        description: "Please select languages for both speakers to begin.",
      });
      return;
    }
    if (sameLanguageBase(langA, langB)) {
      toast({ variant: "warning", title: "Both speakers can’t use the same language." });
      return;
    }
    // "How VERBLIZR uses AI" first (B7, 22-10-2026): "Not now" starts nothing.
    if (!(await ensureAiConsent())) return;
    // Ask the other person first (04-10-2026); their answer starts the session.
    setVoiceConsentOpen(true);
  };

  // The other person's "keep my voice?" sheet, as on the phone.
  const [voiceConsentOpen, setVoiceConsentOpen] = useState(false);

  const beginSession = (personBVoiceConsent: boolean | undefined) => {
    setVoiceConsentOpen(false);
    if (!langA || !langB) return;
    saveLastPair(langA, langB);
    void controller.start({
      langA,
      langB,
      deviceId:
        micForCapture(
          allMics,
          deviceId,
          micBefore,
          routedToCallMic,
          callOn && !hearOnSpeakers
            ? (outputs.find((o) => o.deviceId === hearDevice)?.label ?? null)
            : null,
        ) || undefined,
      call: callOn
        ? {
            // Only Verblizr Microphone carries a translation into a call; an
            // older choice (speakers, Teams' device) or "Nowhere" sends none.
            sendDeviceId: routedToCallMic ? callSettings.sendDeviceId : null,
            translationOnly: callSettings.translationOnly,
            hearDeviceId: hearDevice,
            hearOwn: callSettings.hearOwn,
            theyHearOwn: callSettings.theyHearOwn,
            turnBeep: callSettings.turnBeep,
            speakers: hearOnSpeakers,
          }
        : undefined,
      session: {
        personAName,
        personBName: personBName.trim() || undefined,
        personAGender: personAGender ?? undefined,
        personBGender: personBGender ?? undefined,
        referenceNumber: reference.trim() || undefined,
        scheduledSessionId: booking?.id,
        // The accent of each translation comes from these: the server picks
        // the TTS voice from the session's saved locales, and without them
        // "English (UK)" was spoken in a US voice. Mobile always sends them.
        srcLocale: sessionLocale(langA),
        tgtLocale: sessionLocale(langB),
        personBVoiceConsent,
      },
    });
  };

  // ---- after End: land on the session, as Boardroom lands on its meeting --
  const endedId = useRef<string | null>(null);
  const { leaveDialogOpen, allowLeave, stayOnPage, confirmLeave, setLeaveDialogOpen } =
    useRecorderLeaveGuard(live, `${base}/sessions`);

  useEffect(() => {
    if (snapshot.phase !== "ended" || !snapshot.conversationId) return;
    if (endedId.current === snapshot.conversationId) return;
    endedId.current = snapshot.conversationId;
    toast({
      variant: snapshot.error ? "warning" : "success",
      title: snapshot.error ? "Session ended" : "Session Completed",
      description: snapshot.error?.message ?? snapshot.notice ?? undefined,
    });
    allowLeave();
    navigate(`${base}/sessions/${snapshot.conversationId}`, { replace: true });
  }, [
    snapshot.phase,
    snapshot.conversationId,
    snapshot.error,
    snapshot.notice,
    allowLeave,
    navigate,
    base,
  ]);

  // DEV ONLY: `?toasts=1` cycles PREVIEW_TOASTS (see above).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (
      !PREVIEW_TOASTS_CYCLE &&
      new URLSearchParams(window.location.search).get("toasts") !== "1"
    )
      return;
    if (PREVIEW_TOASTS_CYCLE && !createDeps) return; // never on the real screen
    let i = 0;
    let last: { dismiss: () => void } | null = null;
    const step = () => {
      last?.dismiss();
      last = toast(PREVIEW_TOASTS[i % PREVIEW_TOASTS.length]!);
      i += 1;
    };
    step();
    const id = setInterval(step, 3500);
    return () => {
      clearInterval(id);
      last?.dismiss();
    };
  }, []);

  // The noisy-room advice → one toast while it lasts, gone when the room is
  // clear again or the session pauses (NOISE_AS_TOAST).
  const showNoise = snapshot.noiseWarning && snapshot.phase !== "paused";
  useEffect(() => {
    if (!NOISE_AS_TOAST || !showNoise) return;
    const t = toast({ title: "Room sounds noisy, speak a little louder or move closer." });
    return () => t.dismiss();
  }, [showNoise]);

  // Passing problems → toast, once.
  useEffect(() => {
    if (!snapshot.notice || snapshot.phase === "ended") return;
    toast({ title: snapshot.notice });
    controller.clearNotice();
  }, [snapshot.notice, snapshot.phase, controller]);

  if (!live) {
    return (
      <>
        <VoiceConsentSheet
          open={voiceConsentOpen}
          lang={langB}
          locale={langB ? sessionLocale(langB) : undefined}
          gender={personBGender}
          onAnswer={beginSession}
          onClose={() => setVoiceConsentOpen(false)}
        />
        <SetupView
          base={base}
          error={snapshot.phase === "error" ? snapshot.error : null}
          billingAudience={billingAudienceFor(user)}
          langA={langA}
          langB={langB}
          recentLangs={recentLangs}
          onLangA={setLangA}
          onLangB={setLangB}
          personAName={personAName}
          personAAvatar={user?.profileImageUrl ?? null}
          // The profile is authoritative for Person A's gender, as on the phone.
          personAGender={(user as { gender?: string | null } | null)?.gender ?? personAGender}
          personBName={personBName}
          onPersonBName={setPersonBName}
          personBGender={personBGender}
          onPersonBGender={setPersonBGender}
          reference={reference}
          onReference={setReference}
          devices={devices}
          deviceId={deviceId}
          onDeviceId={setDeviceId}
          micsLoading={micsLoading}
          micsError={micsError}
          callSettings={callModeAvailable ? callSettings : null}
          callApps={callApps}
          onCallSettings={setCallSettings}
          canStart={canStart}
          fromBooking={!!booking}
          onStart={onStart}
        />
      </>
    );
  }

  return (
    <>
      <LiveView
        snapshot={snapshot}
        controller={controller}
        langA={langA ?? ""}
        langB={langB ?? ""}
        personAName={personAName}
        personAAvatar={user?.profileImageUrl ?? null}
        personAGender={personAGender}
        personBName={personBLabel}
        personBGender={personBGender}
        corporate={corporate}
        callTranslationOnly={controller.sendsToCall() ? callSettings.translationOnly : null}
        onCallTranslationOnly={(on) => {
          setCallSettings({ ...callSettings, translationOnly: on });
          controller.setTranslationOnly(on);
        }}
      />
      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Leaving will end this interpretation session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={stayOnPage}>Stay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void controller.end().finally(confirmLeave);
              }}
            >
              End Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// Setup: the Boardroom setup page's layout
// ============================================================================

type SetupProps = {
  base: string;
  error: InterpretSnapshot["error"];
  /** Who can fix a billing refusal, and where (17-10-2026). */
  billingAudience: BillingAudience;
  langA: string | null;
  langB: string | null;
  recentLangs: string[];
  onLangA: (v: string | null) => void;
  onLangB: (v: string | null) => void;
  personAName: string;
  personAAvatar: string | null;
  personAGender: string | null;
  personBName: string;
  onPersonBName: (v: string) => void;
  personBGender: string | null;
  onPersonBGender: (v: string | null) => void;
  reference: string;
  onReference: (v: string) => void;
  devices: ReturnType<typeof useMicDevices>["devices"];
  deviceId: string;
  onDeviceId: (v: string) => void;
  micsLoading: boolean;
  micsError: string | null;
  /** Call mode's settings; null outside the desktop app (not offered). */
  callSettings: CallSettings | null;
  /** Call apps going on (names), for Call mode's line; null = unknown. */
  callApps: string[] | null;
  onCallSettings: (next: CallSettings) => void;
  canStart: boolean;
  fromBooking: boolean;
  onStart: () => void;
};

function startErrorMessage(
  error: NonNullable<InterpretSnapshot["error"]>,
  audience: BillingAudience,
): ReactNode {
  if (
    /^(NotAllowed|PermissionDenied|NotFound|DevicesNotFound|NotReadable|TrackStart|Overconstrained|Security|Abort|NotSupported)Error$/.test(
      error.code,
    )
  ) {
    return describeMicError(Object.assign(new Error(error.message), { name: error.code }));
  }
  // No card, a decline, or 3D Secure: say so and link the screen that fixes it
  // (a personal user's own cards since 17-10-2026, not only an admin's).
  const view = billingErrorView(error.code, audience, error.message);
  if (view) {
    return (
      <>
        <span className="font-semibold">{view.title}.</span> {view.message}
        {view.action ? (
          <>
            {" "}
            <Link to={view.action.to} className="font-semibold underline underline-offset-2">
              {view.action.label}
            </Link>
          </>
        ) : null}
      </>
    );
  }
  return error.message;
}

function SetupView(p: SetupProps) {
  return (
    // Two rows on desktop: the face-pair, then the form, on the right. The
    // left column has no title (DES 03-10-2026, "heading and sub remove
    // karo"): the back link, then Microphone straight under it, spanning
    // both rows so its divider runs the form's full height.
    <div className="mx-auto max-w-6xl px-4 pb-16 pt-6 md:px-5 lg:grid lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-x-10 lg:gap-y-10 lg:pb-6 lg:pt-10">
      <aside className="max-w-2xl lg:row-span-2 lg:flex lg:max-w-none lg:flex-col">
        <div>
          <Link
            to={`${p.base}/sessions`}
            className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Sessions
          </Link>
        </div>

        {/* Divider on the right (DES 01-10-2026), in the field-hairline colour,
            centred in the 40px column gap: -mr-5 pushes the box 20px into
            the gap and pr-5 keeps the content where it was. This outer box
            stretches to the form's full height ("form ki poori height tak");
            the inner one stays sticky. */}
        <div className="mt-6 lg:-mr-5 lg:flex-1 lg:border-r lg:border-app-text/10 lg:pr-5">
          <div className="space-y-3 lg:sticky lg:top-3">
            {/* The hint names the mic in use, from the selection. */}
            <FormFieldGroup
              label="Microphone"
              hint={describeMicInUse(p.devices, p.deviceId, p.micsLoading)}
            >
              <MicPicker
                compact
                devices={p.devices}
                value={p.deviceId}
                onChange={p.onDeviceId}
                loading={p.micsLoading}
                error={p.micsError}
              />
            </FormFieldGroup>

            {p.callSettings ? (
              <CallModeControls
                value={p.callSettings}
                onChange={p.onCallSettings}
                callApps={p.callApps}
              />
            ) : null}

            <p
              className={cn(
                "hidden text-[13px] leading-[18px] text-muted-foreground opacity-70",
                // Call mode wants headphones; this note says the opposite.
                !p.callSettings?.on && "lg:block",
              )}
            >
              Use the laptop speakers, not headphones, so both people hear each translation. Keep
              this tab open during the session.
            </p>
          </div>
        </div>
      </aside>

      {/* The phone's face-pair (DES 01-10-2026, "waisy hi avatars and
          details jaisi mobile per"): top right, beside the title, centred
          over the form so the Microphone row keeps its alignment. */}
      <FacePair
        className="mt-8 max-w-2xl lg:col-start-2 lg:row-start-1 lg:mt-0 lg:self-end"
        personAName={p.personAName}
        personAAvatar={p.personAAvatar}
        personAGender={p.personAGender}
        personBName={p.personBName}
        personBGender={p.personBGender}
      />

      <div
        className={cn(
          "mt-6 max-w-2xl space-y-5 lg:col-start-2 lg:row-start-2 lg:-mt-2",
          formGridLinesHiddenClass,
        )}
      >
        {/* The web form standard (DES 01-10-2026, "inko cards main na rakho,
            web form style main rakho"): bare fields in a two-column grid, as
            the Diary's Schedule Session form, not Fieldset cards. */}
        <FormSheetFields>
          <FormSheetField>
            <FormFieldGroup label="You speak" hint={p.personAName} required>
              <LanguagePicker
                value={p.langA}
                onChange={p.onLangA}
                placeholder="Pick a language"
                clearable
                recentIds={p.recentLangs}
                excludeBaseCode={p.langB}
                title="Language for"
                subtitle={p.personAName}
              />
            </FormFieldGroup>
          </FormSheetField>
          <FormSheetField>
            <FormFieldGroup
              label="Person B speaks"
              hint={p.personBName.trim() || "The other person"}
              required
            >
              <LanguagePicker
                value={p.langB}
                onChange={p.onLangB}
                placeholder="Pick a language"
                clearable
                recentIds={p.recentLangs}
                excludeBaseCode={p.langA}
                title="Language for"
                subtitle={p.personBName.trim() || "Person B"}
              />
            </FormFieldGroup>
          </FormSheetField>
        </FormSheetFields>
        <FormSheetFields className="sm:grid-cols-3">
          <FormSheetField>
            <FormFieldGroup label="Person B (optional)" htmlFor="interpret-person-b">
              <Input
                capitalizeFirst
                id="interpret-person-b"
                value={p.personBName}
                onChange={(e) => p.onPersonBName(e.target.value)}
                placeholder="e.g. Client name"
                maxLength={80}
              />
            </FormFieldGroup>
          </FormSheetField>
          <FormSheetField>
            <FormFieldGroup label="Person B gender (optional)">
              <GenderPicker
                value={p.personBGender}
                onChange={p.onPersonBGender}
                sheetTitle="Person B Gender"
                sheetSubtitle="Voices the translation for Person B."
              />
            </FormFieldGroup>
          </FormSheetField>
          <FormSheetField>
            <FormFieldGroup label="Reference (optional)" htmlFor="interpret-reference">
              <Input
                id="interpret-reference"
                value={p.reference}
                onChange={(e) => p.onReference(e.target.value)}
                placeholder="Case or booking reference"
                maxLength={80}
              />
            </FormFieldGroup>
          </FormSheetField>
        </FormSheetFields>
        <p className="pl-4 pr-[17px] text-[13px] leading-[18px] text-muted-foreground opacity-70 md:pl-5 md:pr-[21px]">
          {p.fromBooking
            ? "Booked session: languages and names come from the booking."
            : "Either person can speak first, for as long as they like. The interpreter detects who is speaking."}
        </p>

        {p.error ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{startErrorMessage(p.error, p.billingAudience)}</span>
          </p>
        ) : null}

        {/* Exactly as wide as the field hairlines above (DES 01-10-2026): the
            cells' px-4/md:px-5 inset, plus the 1px transparent border-r on
            the right. */}
        <div className="mt-2 pl-4 pr-[17px] md:pl-5 md:pr-[21px] lg:mt-1">
          <SwipeButton
            text="Slide to Start Interpretation"
            icon={BrandMarkSlideIcon} // DES 08-10-2026: app icon, was Languages
            disabled={!p.canStart}
            onSwipeComplete={p.onStart}
            accessibilityLabel="Start interpretation"
            accessibilityHint="Slide or press Enter to start a live interpretation session"
          />
        </div>
        <p className="text-center text-xs text-muted-foreground sm:text-left lg:hidden">
          Use the laptop speakers, not headphones, so both people hear each translation.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Live: the Boardroom recorder's layout
// ============================================================================

type LiveProps = {
  snapshot: InterpretSnapshot;
  controller: ReturnType<typeof useInterpretSession>["controller"];
  langA: string;
  langB: string;
  personAName: string;
  personAAvatar: string | null;
  personAGender: string | null;
  personBName: string;
  personBGender: string | null;
  corporate: boolean;
  /** Call mode sending to a call: "Only send the translation", switchable
   *  during the session (DES 03-10-2026). null = not in Call mode. */
  callTranslationOnly: boolean | null;
  onCallTranslationOnly: (on: boolean) => void;
};

function LiveView({
  snapshot,
  controller,
  langA,
  langB,
  personAName,
  personAAvatar,
  personAGender,
  personBName,
  personBGender,
  corporate,
  callTranslationOnly,
  onCallTranslationOnly,
}: LiveProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isSmUp = useMediaQuery("(min-width: 640px)");
  const { phase } = snapshot;

  // ---- the recorder's sheet / tray measuring, unchanged -------------------
  const [sheetSlot, setSheetSlot] = useState<HTMLDivElement | null>(null);
  const [sheetBox, setSheetBox] = useState<{
    left: number;
    width: number;
    bottom: number;
    paddingBottom: number;
    maxHeight: number;
    minHeight: number;
  } | null>(null);
  useEffect(() => {
    if (!isDesktop || !sheetSlot) {
      setSheetBox(null);
      return;
    }
    const footerH = document.querySelector("footer")?.offsetHeight ?? 0;
    const measure = () => {
      const r = sheetSlot.getBoundingClientRect();
      const root = sheetSlot.closest<HTMLElement>("[data-scroll-root]");
      const headerH = root ? parseFloat(getComputedStyle(root).paddingTop) : 0;
      setSheetBox({
        left: r.left,
        width: r.width,
        bottom: 0,
        paddingBottom: footerH + 16,
        maxHeight: window.innerHeight - headerH - STICKY_GAP,
        // Taller mic sheet (DES 01-10-2026): at least SHEET_MIN_H, never
        // past the space under the header.
        minHeight: Math.min(SHEET_MIN_H, window.innerHeight - headerH - STICKY_GAP),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sheetSlot);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isDesktop, sheetSlot]);

  const [trayEl, setTrayEl] = useState<HTMLDivElement | null>(null);
  const [wrapEl, setWrapEl] = useState<HTMLDivElement | null>(null);
  const [trayH, setTrayH] = useState(0);
  const [trayFooterPad, setTrayFooterPad] = useState(0);
  useEffect(() => {
    const tray = isSmUp ? wrapEl : trayEl;
    if (isDesktop || !tray) return;
    const footer = document.querySelector("footer");
    const measure = () => {
      setTrayFooterPad(footer?.offsetHeight ?? 0);
      setTrayH(tray.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tray, { box: "border-box" });
    if (footer) ro.observe(footer);
    return () => ro.disconnect();
  }, [isDesktop, isSmUp, trayEl, wrapEl]);

  // ---- timer, glow and "Speaking" -----------------------------------------
  const [elapsedMs, setElapsedMs] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const audioLevelRef = useRef(0);
  const speakingRef = useRef(false);
  useEffect(() => {
    let raf = 0;
    let lastClock = 0;
    const tick = (t: number) => {
      const listening = controller.getSnapshot().phase === "listening";
      const rms = listening ? controller.getLevel() : 0;
      audioLevelRef.current = normalizeMicRms(rms, GLOW_CEILING);
      const sp = nextSpeaking(speakingRef.current, rms);
      if (sp !== speakingRef.current) {
        speakingRef.current = sp;
        setSpeaking(sp);
      }
      if (t - lastClock > 250) {
        lastClock = t;
        setElapsedMs(controller.getBillableMs());
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [controller]);

  // ---- keyboard: Space = end turn, P = pause / resume ---------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(el.tagName)))
        return;
      if (e.code === "Space") {
        e.preventDefault();
        controller.endTurn();
      } else if (e.key === "p" || e.key === "P") {
        if (controller.getSnapshot().phase === "paused") void controller.resume();
        else controller.pause();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [controller]);

  const status = statusView(phase, snapshot.connection, speaking);
  const StatusGlyph = status ? STATUS_ICONS[status.icon] : null;
  const lastTurn = snapshot.turns[snapshot.turns.length - 1];
  const rtt = rttLabel(lastTurn?.latencyMs);
  const playingLang = snapshot.playing?.tgtLang ?? null;
  const playingText = snapshot.playing?.tgtText ?? "";
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);

  // The phone's mic: tap = end your turn (resume when paused), hold = End Session?
  const micState: InterpretMicState =
    phase === "paused" ? "paused" : phase === "listening" || phase === "playing" ? "live" : "busy";
  const onMicTap = () => {
    if (phase === "paused") void controller.resume();
    else if (phase === "listening") {
      controller.endTurn();
      toast({ title: "Ending turn…" });
    }
  };
  // The phone's status row: tap = pause / resume.
  const onStatusTap = () => {
    if (phase === "paused") void controller.resume();
    else controller.pause();
  };
  // The desktop app's floating widget (02-10-2026): status, clock and the
  // latest translation as a one-line caption; pause / end come back here.
  // The clock is read from the controller, not `elapsedMs`: a hidden window's
  // animation frames stop, and the widget runs its own clock from `at`.
  const caption = playingText || lastTurn?.tgtText || "";
  useDesktopSession(
    LIVE.has(phase)
      ? {
          kind: "interpret",
          title: "Interpreter",
          status:
            phase === "paused"
              ? "paused"
              : phase === "starting" || phase === "ending"
                ? "busy"
                : "live",
          statusLabel: phase === "paused" ? null : (status?.label ?? null),
          elapsedMs: controller.getBillableMs(),
          at: Date.now(),
          running: phase !== "paused" && phase !== "starting" && phase !== "ending",
          langs: `${languageBaseCode(langA).toUpperCase()} → ${languageBaseCode(langB).toUpperCase()}`,
          langFlags: [langA, langB].map((l) => ({
            code: languageBaseCode(l),
            locale: l && l.includes("-") ? l : undefined,
          })),
          caption: caption ? { text: caption, rtl: isRTLText(caption) } : null,
        }
      : null,
    (cmd) => {
      if (cmd.type === "toggle-hold") {
        if (phase === "paused") void controller.resume();
        else controller.pause();
      } else if (cmd.type === "end") {
        if (phase !== "ending") void controller.end();
      }
    },
  );

  const onReplay = (turnId: string) => {
    void controller.replayTurn(turnId).then((r) => {
      if (r === "not-paused") toast({ title: "Pause the session to play transcript audio." });
    });
  };

  return (
    <>
      <AlertDialog open={endConfirmOpen} onOpenChange={setEndConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this interpretation session?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void controller.end()}>End Session</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Recorder column 26rem, transcript takes the rest (DES 09-10-2026,
          "thora sa patla"; was two equal columns, ~31rem each). Centred; a
          left start was tried and undone (DES 11-10-2026). */}
      <div className="flex min-h-[calc(100dvh-8.5rem)] w-full items-center justify-center py-4 lg:min-h-0 lg:items-start lg:py-2">
        <div className="relative flex w-full min-w-0 max-w-lg flex-col items-center gap-6 overflow-visible p-5 sm:p-6 lg:grid lg:max-w-5xl lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:items-start lg:gap-x-10 lg:gap-y-4 lg:py-2">
          <div
            ref={setSheetSlot}
            aria-hidden
            className="hidden lg:col-start-1 lg:row-start-1 lg:block"
          />
          <div
            ref={setWrapEl}
            className="contents motion-reduce:animate-none sm:fixed sm:inset-x-0 sm:bottom-0 sm:z-30 sm:grid sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] sm:items-center sm:gap-6 sm:rounded-t-[20px] sm:bg-secondary sm:px-6 sm:pt-4 sm:shadow-[0_-6px_12px_rgba(0,0,0,0.08)] dark:sm:bg-card lg:right-auto lg:flex lg:flex-col lg:gap-4 lg:overflow-hidden lg:rounded-t-[28px] lg:bg-secondary lg:px-6 lg:pb-4 lg:pt-3 lg:shadow-[0_-8px_24px_rgba(0,0,0,0.06)] lg:animate-in lg:fade-in-0 lg:slide-in-from-bottom-6 dark:lg:bg-card dark:lg:shadow-[0_-8px_24px_rgba(0,0,0,0.3)]"
            style={
              isDesktop
                ? (sheetBox ?? undefined)
                : isSmUp
                  ? { paddingBottom: trayFooterPad + 8 }
                  : undefined
            }
          >
            <span
              aria-hidden
              className="mx-auto hidden h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/25 lg:block"
            />
            <div className="order-1 flex w-full flex-col items-center gap-6 lg:min-h-0 lg:flex-1 lg:shrink lg:justify-evenly lg:gap-4 lg:overflow-y-auto">
              <header className="flex w-full flex-col items-center gap-2.5 text-center">
                <span
                  className={
                    phase === "paused"
                      ? "inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
                      : "inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-brand"
                  }
                >
                  <span
                    aria-hidden
                    className={
                      phase === "paused"
                        ? "h-1.5 w-1.5 rounded-full bg-muted-foreground/55"
                        : "h-1.5 w-1.5 animate-pulse rounded-full bg-brand"
                    }
                  />
                  {phase === "paused" ? "Paused" : phase === "starting" ? "Starting" : "Live"}
                </span>
                {/* No visible "Interpreter" title (DES 09-10-2026): the top bar
                    already says it; the pill + language row are enough. */}
                <h1 className="sr-only">Interpreter</h1>
                {/* The phone's language row: RTT · A → B · billing icon. */}
                <div className="grid w-full grid-cols-[3.5rem_minmax(0,1fr)_3.5rem] items-center">
                  <span className="inline-flex items-center gap-1 justify-self-start text-[13px] font-bold text-muted-foreground">
                    {rtt ? (
                      <>
                        <Zap className="h-4 w-4 text-brand" aria-hidden />
                        <span title="Last turn's round trip">{rtt}</span>
                      </>
                    ) : null}
                  </span>
                  <div className="flex min-w-0 items-center justify-center gap-2">
                    <LangPill
                      label={getLanguageEnglishName(langA)}
                      name={personAName}
                      avatar={personAAvatar}
                      active={
                        !!playingLang && languageBaseCode(playingLang) === languageBaseCode(langA)
                      }
                    />
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <LangPill
                      label={getLanguageEnglishName(langB)}
                      name={personBName}
                      avatar={null}
                      active={
                        !!playingLang && languageBaseCode(playingLang) === languageBaseCode(langB)
                      }
                    />
                  </div>
                  <span
                    className="justify-self-end"
                    title={corporate ? "Billed to your company" : "Billed to you"}
                    aria-label={corporate ? "Company billing" : "Personal billing"}
                  >
                    {corporate ? (
                      <Briefcase className="h-[18px] w-[18px] text-info" aria-hidden />
                    ) : (
                      <User className="h-[18px] w-[18px] text-muted-foreground" aria-hidden />
                    )}
                  </span>
                </div>
              </header>

              {/* The phone's timer row (Clock + flip clock, pause-excluded). */}
              <div className="inline-flex items-center gap-1.5 text-foreground">
                <Clock className="h-4 w-4" aria-hidden />
                <FlipClock
                  value={formatElapsed(elapsedMs)}
                  fontSize={16}
                  className="font-thin tabular-nums"
                />
              </div>

              <InterpretMicButton
                state={micState}
                levelRef={audioLevelRef}
                caption={
                  playingText
                    ? {
                        text: playingText,
                        lang: playingLang,
                        style: captionScriptStyle(playingLang),
                      }
                    : null
                }
                getProgress={() => controller.playbackProgress()}
                onTap={onMicTap}
                pausedGlyph={INTERPRET_DOCK ? "micOff" : "play"}
                onLongPress={() => {
                  if (phase !== "ending" && phase !== "starting") setEndConfirmOpen(true);
                }}
                ariaLabel={
                  phase === "paused"
                    ? "Resume"
                    : phase === "listening"
                      ? "End your turn. Hold to end the session"
                      : "Processing, please wait"
                }
              />

              {INTERPRET_DOCK ? (
                <div className="-mt-2 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={onStatusTap}
                    disabled={phase === "ending" || phase === "starting" || phase === "playing"}
                    aria-label={phase === "paused" ? "Resume" : "Pause"}
                    title={phase === "paused" ? "Resume" : "Pause"}
                    // The bare icon, no circle around it (DES 11-10-2026: "isko
                    // circle main na rakho"); the 56px tap area stays.
                    className="inline-flex h-14 w-14 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                  >
                    {phase === "paused" ? (
                      <Play className="ml-0.5 h-7 w-7" strokeWidth={1.8} aria-hidden />
                    ) : (
                      <Pause className="h-7 w-7" strokeWidth={1.8} aria-hidden />
                    )}
                  </button>
                  <div
                    className={cn(
                      "inline-flex min-h-6 items-center gap-1.5 text-sm font-semibold tracking-[0.2px]",
                      phase === "paused" || status?.muted ? "text-muted-foreground" : "text-foreground",
                    )}
                    aria-live="polite"
                  >
                    {phase === "paused" ? (
                      "Click here to resume"
                    ) : status && StatusGlyph ? (
                      <>
                        <StatusGlyph
                          className={cn("h-[18px] w-[18px]", status.icon === "loader" && "animate-spin")}
                          aria-hidden
                        />
                        {status.label}
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {!INTERPRET_DOCK && status && StatusGlyph ? (
                <button
                  type="button"
                  onClick={onStatusTap}
                  disabled={phase === "ending" || phase === "starting" || phase === "playing"}
                  aria-label={phase === "paused" ? "Resume" : "Pause"}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold tracking-[0.2px] transition-colors hover:bg-muted disabled:hover:bg-transparent",
                    status.muted ? "text-muted-foreground" : "text-foreground",
                  )}
                >
                  <StatusGlyph
                    className={cn("h-[18px] w-[18px]", status.icon === "loader" && "animate-spin")}
                    aria-hidden
                  />
                  {status.label}
                </button>
              ) : null}
              {/* With the round Pause button, the how-to line moved into the
                  tips icon by "Conversation" (DES 09-10-2026: "looking bad"). */}
              {!INTERPRET_DOCK ? (
                <p className="-mt-3 text-center text-[11px] leading-snug text-muted-foreground">
                  Tap the mic to end your turn · hold it to end · tap the status to pause
                </p>
              ) : null}

              {callTranslationOnly !== null ? (
                <label className="flex w-full max-w-[18rem] items-center justify-between gap-3 py-1">
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium">Only send the translation</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {callTranslationOnly
                        ? "The call hears only your translation."
                        : "The call hears you, then your translation."}
                    </span>
                  </span>
                  <Switch
                    checked={callTranslationOnly}
                    onCheckedChange={onCallTranslationOnly}
                    aria-label="Only send the translation"
                  />
                </label>
              ) : null}

              {!NOISE_AS_TOAST && snapshot.noiseWarning && phase !== "paused" ? (
                <p className="inline-flex max-w-[18rem] items-center gap-2 rounded-2xl bg-muted px-3 py-1.5 text-center text-[13px] font-medium text-foreground">
                  <Volume2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  Room sounds noisy, speak a little louder or move closer.
                </p>
              ) : null}
            </div>

            <div
              ref={setTrayEl}
              className="fixed inset-x-0 bottom-0 z-30 order-3 flex flex-col items-center gap-5 rounded-t-[20px] bg-secondary px-6 pt-4 shadow-[0_-6px_12px_rgba(0,0,0,0.08)] dark:bg-card sm:static sm:inset-auto sm:z-auto sm:w-full sm:gap-4 sm:rounded-none sm:bg-transparent sm:p-0 sm:shadow-none dark:sm:bg-transparent lg:shrink-0 [&>*]:max-w-lg sm:[&>*]:max-w-none"
              style={isSmUp ? undefined : { paddingBottom: trayFooterPad + 8 }}
            >
              <div className="flex w-full flex-col gap-2 pt-2">
                <SwipeButton
                  text="Slide to End Interpretation"
                  loading={phase === "ending"}
                  loadingText="Ending session…"
                  disabled={phase === "ending" || phase === "starting"}
                  onSwipeComplete={() => void controller.end()}
                  accessibilityLabel="End interpretation session"
                  accessibilityHint="Slide or press Enter to end and save the session"
                />
              </div>
            </div>
          </div>

          <div
            aria-hidden
            className="order-4 w-full shrink-0 lg:hidden"
            style={{ height: isDesktop ? 0 : Math.max(0, trayH - trayFooterPad) }}
          />

          <div className="order-2 w-full space-y-3 lg:col-start-2 lg:row-start-1">
            <InterpretTranscript
              turns={snapshot.turns}
              personAName={personAName}
              personBName={personBName}
              personAAvatar={personAAvatar}
              personAGender={personAGender}
              personBGender={personBGender}
              playbackEnabled={phase === "paused"}
              replayingId={snapshot.replayingId}
              hasAudio={(id) => controller.hasTurnAudio(id)}
              onPlay={onReplay}
              // Calm view while live (DES 19-10-2026); paused shows every
              // turn for replay.
              focus={phase === "listening" || phase === "translating" || phase === "playing"}
              tips={
                INTERPRET_DOCK
                  ? [
                      "Click the round button to pause. Paused time is not charged.",
                      "Click the mic to end your turn now, if a noisy room keeps it open.",
                      "Hold the mic to end the session.",
                      "While paused, click ▶ on a turn to hear its translation again.",
                    ]
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Urdu captions: the app's one Naskh face (05-10-2026, DES: the caption's
 * font "waisa nahin hai jaisa side per transcript main"). It used to pin
 * "Noto Nastaliq Urdu" (the 27-09 rule, retired 05-10): no font is named now,
 * so it follows the same stack as the transcript (`--vz-arabic-font`, Apple's
 * system Arabic on a Mac). Naskh needs less leading than Nastaliq did.
 */
function captionScriptStyle(lang: string | null): CSSProperties | undefined {
  if (languageBaseCode(lang) !== "ur") return undefined;
  return {
    lineHeight: 1.9,
    fontWeight: 500,
  };
}

/** The phone's `LangPill`: avatar or initial, uppercase language, brand while it plays. */
// ============================================================================
// Face-pair: mirrors FRONTEND SessionDetailsModal (96px circles, B overlaps
// A by 32px, names "A · B", then the genders "A  →  B").
// ============================================================================

function genderLabel(code: string | null): string | null {
  if (!code) return null;
  return GENDERS.find((g) => g.code === code.toLowerCase())?.label ?? null;
}

function FaceCircle({
  name,
  avatar,
  gender,
  className,
}: {
  name: string;
  avatar: string | null;
  gender: string | null;
  className?: string;
}) {
  const g = gender?.toLowerCase();
  const ring = "h-24 w-24 border-[3px] border-background";
  if (!avatar && (g === "male" || g === "female")) {
    const Icon = g === "female" ? Venus : Mars;
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-muted",
          ring,
          className,
        )}
      >
        <Icon className="h-[58px] w-[58px] text-muted-foreground" aria-hidden />
      </span>
    );
  }
  return (
    <Avatar
      src={avatar}
      alt={name}
      initials={speakerInitials(name)}
      className={cn(ring, "bg-black text-[36px] text-white", className)}
    />
  );
}

function FacePair({
  personAName,
  personAAvatar,
  personAGender,
  personBName,
  personBGender,
  className,
}: {
  personAName: string;
  personAAvatar: string | null;
  personAGender: string | null;
  personBName: string;
  personBGender: string | null;
  className?: string;
}) {
  const bName = personBName.trim();
  const genders = [genderLabel(personAGender), genderLabel(personBGender)].filter(Boolean);
  return (
    <div className={cn("flex flex-col items-center text-center", className)}>
      <div className="mb-2 flex items-center">
        <FaceCircle name={personAName} avatar={personAAvatar} gender={personAGender} />
        {/* Person B never borrows the user's photo. */}
        <FaceCircle
          name={bName || "B"}
          avatar={null}
          gender={personBGender}
          className="relative z-[1] -ml-8"
        />
      </div>
      <p className="max-w-[90%] truncate text-base font-semibold text-foreground">
        {personAName}
        <span className="font-normal text-muted-foreground"> · </span>
        <span className={bName ? undefined : "font-medium text-muted-foreground/70"}>
          {bName || "Person B"}
        </span>
      </p>
      {genders.length > 0 ? (
        <p className="mt-1 truncate whitespace-pre text-xs font-medium text-muted-foreground">
          {genders.join("  →  ")}
        </p>
      ) : null}
    </div>
  );
}

function LangPill({
  label,
  name,
  avatar,
  active,
}: {
  label: string;
  name: string;
  avatar: string | null;
  active: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[30px] min-w-0 max-w-[47%] items-center gap-1.5 rounded-full border pr-3 text-[12px] font-bold uppercase tracking-[0.5px] transition-colors",
        active
          ? "border-transparent bg-brand/10 text-brand"
          : "border-border bg-background text-muted-foreground",
      )}
      title={`${name} · ${label}`}
    >
      <Avatar
        src={avatar}
        alt={name}
        initials={speakerInitials(name)}
        size="xs"
        className="h-[30px] w-[30px] shrink-0"
      />
      <span className="truncate">{label.replace(/\s*\(.*\)\s*$/, "")}</span>
    </span>
  );
}
